import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Share, X } from 'lucide-react';

// Same icon (and proportions) the installed app uses on the home screen, so
// the banner reads as "this becomes that icon" rather than a generic
// download glyph.
const APP_ICON_SRC = '/assets/images/hustli_app_icon_maskable_512.png';

// Chrome/Android fires this instead of showing its own install UI immediately;
// capturing it lets us show a custom "Install Hustli" button on our own timing.
// Not yet in TS's lib.dom.d.ts, so it's typed locally.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const ANDROID_DISMISS_KEY = 'hustli-install-prompt-dismissed-until';
const IOS_DISMISS_KEY = 'hustli-ios-install-banner-dismissed-until';
const DISMISS_DAYS = 14;

function isDismissed(key: string): boolean {
  try {
    const until = localStorage.getItem(key);
    return !!until && Date.now() < Number(until);
  } catch {
    return false;
  }
}

function dismissFor(key: string, days: number): void {
  try {
    localStorage.setItem(key, String(Date.now() + days * 24 * 60 * 60 * 1000));
  } catch {
    // localStorage unavailable (private mode, etc.) - fail silently
  }
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

function isIosSafari(): boolean {
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIos && isSafari;
}

/**
 * Custom "Add Hustli to your home screen" banner for the web build.
 *
 * - Android/Chrome: hooks `beforeinstallprompt` and shows our own button
 *   instead of relying on the browser's default install timing.
 * - iOS Safari: there's no install-prompt API, so we show simple
 *   "Tap Share, then Add to Home Screen" instructions instead.
 *
 * Never renders inside the Capacitor native app or once already installed.
 */
export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosBanner, setShowIosBanner] = useState(false);

  useEffect(() => {
    if (Capacitor.isNativePlatform() || isStandalone()) return;

    if (isIosSafari()) {
      if (!isDismissed(IOS_DISMISS_KEY)) setShowIosBanner(true);
      return;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      if (isDismissed(ANDROID_DISMISS_KEY)) return;
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  if (deferredPrompt) {
    return (
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slide-up">
        <div className="bg-white rounded-2xl shadow-card border border-gray-200 p-4 flex items-center gap-3">
          <img
            src={APP_ICON_SRC}
            alt=""
            aria-hidden="true"
            className="flex-shrink-0 w-16 h-16 rounded-2xl shadow-card"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-dark">Install Hustli</p>
            <p className="text-xs text-stone">Add it to your home screen for quick access.</p>
          </div>
          <button
            type="button"
            aria-label="Install Hustli app"
            onClick={async () => {
              await deferredPrompt.prompt();
              await deferredPrompt.userChoice;
              setDeferredPrompt(null);
            }}
            className="flex-shrink-0 rounded-xl bg-primary text-white text-sm font-medium px-3 py-2 hover:bg-primary-hover transition-colors"
          >
            Install
          </button>
          <button
            type="button"
            aria-label="Dismiss install prompt"
            onClick={() => {
              dismissFor(ANDROID_DISMISS_KEY, DISMISS_DAYS);
              setDeferredPrompt(null);
            }}
            className="flex-shrink-0 text-stone hover:text-dark p-1"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  if (showIosBanner) {
    return (
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slide-up">
        <div className="bg-white rounded-2xl shadow-card border border-gray-200 p-4 flex items-center gap-3">
          <img
            src={APP_ICON_SRC}
            alt=""
            aria-hidden="true"
            className="flex-shrink-0 w-16 h-16 rounded-2xl shadow-card"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-dark">Install Hustli</p>
            <p className="text-xs text-stone">
              Tap <Share className="inline w-3 h-3" aria-hidden="true" /> Share, then "Add to Home
              Screen".
            </p>
          </div>
          <button
            type="button"
            aria-label="Dismiss install instructions"
            onClick={() => {
              dismissFor(IOS_DISMISS_KEY, DISMISS_DAYS);
              setShowIosBanner(false);
            }}
            className="flex-shrink-0 text-stone hover:text-dark p-1"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
